import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import { useWorkspaceTour } from '../../hooks/useWorkspaceTour';
import { useTheme } from '../../hooks/useTheme';
import { cn } from '../ui/Button';
export function WorkspaceTour() {
  const {
    isActive,
    currentStep,
    steps,
    skipTour,
    nextStep,
    prevStep,
    completeTour
  } = useWorkspaceTour();
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const step = steps[currentStep];
  const updateTargetRect = useCallback(() => {
    if (!isActive || !step) return;
    // For 'center' position, we don't need a specific target rect, just center on screen
    if (step.position === 'center') {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (el) {
      setTargetRect(el.getBoundingClientRect());
    } else {
      // If element not found, try again shortly (might be rendering)
      setTimeout(() => {
        const retryEl = document.querySelector(step.target);
        if (retryEl) setTargetRect(retryEl.getBoundingClientRect());
      }, 100);
    }
  }, [isActive, step]);
  useEffect(() => {
    updateTargetRect();
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
      updateTargetRect();
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', updateTargetRect, true);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', updateTargetRect, true);
    };
  }, [updateTargetRect]);
  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skipTour();
      if (e.key === 'ArrowRight' || e.key === 'Enter') nextStep();
      if (e.key === 'ArrowLeft') prevStep();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, nextStep, prevStep, skipTour]);
  if (!isActive || !step) return null;
  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (step.position === 'center' || !targetRect) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: 1
      };
    }
    const gap = 24;
    const tooltipWidth = 360;
    const tooltipHeight = 200; // Approximate height
    let top = 0;
    let left = 0;
    switch (step.position) {
      case 'bottom':
        top = targetRect.bottom + gap;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        // Adjust if off-screen
        if (left + tooltipWidth > windowSize.width - 20) {
          left = windowSize.width - tooltipWidth - 20;
        }
        if (left < 20) left = 20;
        break;
      case 'top':
        top = targetRect.top - gap - tooltipHeight;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.right + gap;
        // Adjust if off-screen vertically
        if (top + tooltipHeight > windowSize.height - 20) {
          top = windowSize.height - tooltipHeight - 20;
        }
        if (top < 20) top = 20;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left - gap - tooltipWidth;
        break;
    }
    return {
      top,
      left,
      opacity: 1
    };
  };
  // Calculate arrow position based on the adjusted tooltip position
  const getArrowStyle = () => {
    if (step.position === 'center' || !targetRect) return {
      display: 'none'
    };
    const style = getTooltipStyle();
    const arrowSize = 10;
    const arrowColor = isLight ? 'rgba(255, 255, 255, 0.95)' : 'rgba(15, 23, 42, 0.95)';
    const borderColor = isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)';
    if (step.position === 'bottom') {
      const targetCenter = targetRect.left + targetRect.width / 2;
      let arrowLeft = targetCenter - (style.left as number);
      arrowLeft = Math.max(20, Math.min(340, arrowLeft));
      return {
        top: -arrowSize,
        left: arrowLeft,
        borderLeft: `${arrowSize}px solid transparent`,
        borderRight: `${arrowSize}px solid transparent`,
        borderBottom: `${arrowSize}px solid ${arrowColor}`,
        filter: `drop-shadow(0 -1px 0 ${borderColor})`
      };
    }
    if (step.position === 'right') {
      const targetCenter = targetRect.top + targetRect.height / 2;
      let arrowTop = targetCenter - (style.top as number);
      arrowTop = Math.max(20, Math.min(180, arrowTop));
      return {
        left: -arrowSize,
        top: arrowTop,
        borderTop: `${arrowSize}px solid transparent`,
        borderBottom: `${arrowSize}px solid transparent`,
        borderRight: `${arrowSize}px solid ${arrowColor}`,
        filter: `drop-shadow(-1px 0 0 ${borderColor})`
      };
    }
    if (step.position === 'left') {
      const targetCenter = targetRect.top + targetRect.height / 2;
      let arrowTop = targetCenter - (style.top as number);
      arrowTop = Math.max(20, Math.min(180, arrowTop));
      return {
        right: -arrowSize,
        top: arrowTop,
        borderTop: `${arrowSize}px solid transparent`,
        borderBottom: `${arrowSize}px solid transparent`,
        borderLeft: `${arrowSize}px solid ${arrowColor}`,
        filter: `drop-shadow(1px 0 0 ${borderColor})`
      };
    }
    return {
      display: 'none'
    };
  };
  const isLastStep = currentStep === steps.length - 1;
  return <div className="fixed inset-0 z-[10000] pointer-events-none" data-id="element-1069">
      {/* Spotlight Overlay */}
      {targetRect && step.position !== 'center' && <div className="absolute transition-all duration-500 ease-in-out pointer-events-auto" style={{
      top: targetRect.top - 6,
      left: targetRect.left - 6,
      width: targetRect.width + 12,
      height: targetRect.height + 12,
      borderRadius: '16px',
      boxShadow: isLight ? '0 0 0 9999px rgba(0, 0, 0, 0.4)' : '0 0 0 9999px rgba(0, 0, 0, 0.7)',
      zIndex: 10000
    }} data-id="element-1070" />}

      {/* Full overlay for center steps */}
      {step.position === 'center' && <div className="absolute inset-0 pointer-events-auto" style={{
      background: isLight ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.7)',
      zIndex: 10000
    }} data-id="element-1071" />}

      {/* Tooltip */}
      <AnimatePresence mode="wait" data-id="element-1072">
        <motion.div key={currentStep} initial={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -10,
        scale: 0.95
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30
      }} className={cn('absolute w-[360px] rounded-2xl pointer-events-auto shadow-2xl', isLight ? 'bg-white border border-black/[0.08] shadow-slate-300/50' : 'bg-[#0F172A] border border-white/[0.1] shadow-black/50')} style={{
        ...getTooltipStyle(),
        zIndex: 10001
      }} data-id="element-1073">
          {/* Arrow */}
          <div className="absolute w-0 h-0" style={getArrowStyle()} data-id="element-1074" />

          <div className="p-5" data-id="element-1075">
            {/* Header */}
            <div className="flex items-start justify-between mb-4" data-id="element-1076">
              <div className="flex items-center gap-2" data-id="element-1077">
                <div className="w-6 h-6 rounded-full bg-[#EC4899]/10 flex items-center justify-center text-[#EC4899] text-xs font-bold" data-id="element-1078">
                  {currentStep + 1}
                </div>
                <span className={cn('text-xs font-medium uppercase tracking-wider', isLight ? 'text-slate-500' : 'text-slate-400')} data-id="element-1079">
                  Step {currentStep + 1} of {steps.length}
                </span>
              </div>
              <button onClick={skipTour} className={cn('transition-colors p-1 rounded-md', isLight ? 'text-slate-400 hover:text-slate-700 hover:bg-slate-100' : 'text-slate-500 hover:text-white hover:bg-white/10')} data-id="element-1080">
                <X size={14} data-id="element-1081" />
              </button>
            </div>

            {/* Content */}
            <div className="mb-6" data-id="element-1082">
              <h3 className={cn('text-base font-semibold mb-2', isLight ? 'text-slate-900' : 'text-white')} data-id="element-1083">
                {step.title}
              </h3>
              <div className={cn('text-sm leading-relaxed', isLight ? 'text-slate-600' : 'text-slate-300')} data-id="element-1084">
                {step.athenaMessage}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-200/10" data-id="element-1085">
              <div className="flex gap-1.5" data-id="element-1086">
                {steps.map((_, idx) => <div key={idx} className={cn('w-1.5 h-1.5 rounded-full transition-colors', idx === currentStep ? 'bg-[#EC4899]' : isLight ? 'bg-slate-200' : 'bg-slate-700')} data-id="element-1087" />)}
              </div>

              <div className="flex items-center gap-2" data-id="element-1088">
                {currentStep > 0 && <Button variant="ghost" size="sm" onClick={prevStep} className={cn('h-8 px-3', isLight ? 'text-slate-500 hover:text-slate-900' : 'text-slate-400 hover:text-white')} data-id="element-1089">
                    Back
                  </Button>}
                <Button size="sm" onClick={isLastStep ? completeTour : nextStep} className="bg-[#EC4899] hover:bg-pink-600 text-white h-8 px-5 rounded-lg shadow-md shadow-pink-500/20" data-id="element-1090">
                  {isLastStep ? 'Finish' : 'Next'}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>;
}